import * as yup from "yup";
import type { Intervention } from "../types";
import { countHtmlTextWords, countWords } from "./utils";
import { transformTimestampValidity } from "../formatting/date-time-manipulation";
import {
  wordLimitMessage,
  richTextMaxWords,
  shortDescriptionMaxWords,
} from "../formatting/defaults";

export const urlRegex =
  /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/i;
export const websiteUrlRegex = /^(projects|archived-projects)\/[a-zA-Z-]+$/;

export { transformTimestampValidity };

export const hasTimestamp = (
  timestamp: number | null | undefined,
): timestamp is number => timestamp !== null && timestamp !== undefined;

export const isValidTimestamp = (
  timestamp: number | null | undefined,
): timestamp is number => hasTimestamp(timestamp) && Number.isFinite(timestamp);

export const interventionItemSchema = yup
  .object({
    action: yup.array().of(yup.string()).nullable().default([]),
    ecosystem: yup.array().of(yup.string()).nullable().default([]),
    interventionCounter: yup.number(),
  })
  .test({
    name: "paired-selection",
    test: (value: Intervention | undefined, context) => {
      const hasAction = Array.isArray(value?.action) && value.action.length > 0;
      const hasEcosystem =
        Array.isArray(value?.ecosystem) && value.ecosystem.length > 0;

      if (hasAction && !hasEcosystem) {
        return context.createError({
          path: `${context.path}.ecosystem`,
          message:
            "Must select corresponding 'ecosystem' where 'action' is selected",
        });
      }

      if (hasEcosystem && !hasAction) {
        return context.createError({
          path: `${context.path}.action`,
          message:
            "Must select corresponding 'action' where 'ecosystem' is selected",
        });
      }

      return true;
    },
  });

export const interventionsSchema = yup.array().of(interventionItemSchema);

const hasCoordinateValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

const optionalCoordinateTransform = (
  parsedValue: number,
  originalValue: unknown,
): number | undefined =>
  hasCoordinateValue(originalValue) ? parsedValue : undefined;

export const createLatitudeSchema = (allowEmpty: boolean) => {
  const schema = yup
    .number()
    .test(
      "longitude-dependency",
      "Both latitude and longitude must be provided",
      (value, context) =>
        !hasCoordinateValue(value) ||
        hasCoordinateValue(context.parent.longitude),
    )
    .min(-90, "Latitude must be between -90 and 90 degrees")
    .max(90, "Latitude must be between -90 and 90 degrees")
    .typeError("Must be a number");

  return allowEmpty
    ? schema.transform(optionalCoordinateTransform).optional()
    : schema;
};

export const createLongitudeSchema = (allowEmpty: boolean) => {
  const schema = yup
    .number()
    .test(
      "latitude-dependency",
      "Both longitude and latitude must be provided",
      (value, context) =>
        !hasCoordinateValue(value) ||
        hasCoordinateValue(context.parent.latitude),
    )
    .min(-180, "Longitude must be between -180 and 180 degrees")
    .max(180, "Longitude must be between -180 and 180 degrees")
    .typeError("Must be a number");

  return allowEmpty
    ? schema.transform(optionalCoordinateTransform).optional()
    : schema;
};

export const createUrlSchema = (
  regex: RegExp,
  message: string,
  allowEmpty: boolean,
) =>
  yup.string().matches(regex, {
    message,
    excludeEmptyString: allowEmpty,
  });

export const riskMonitoringSchema = yup.object().nullable().shape({
  "is-risk-alert-enabled": yup.boolean(),
  "buffer-value-km": yup.number(),
  "geospatial-file": yup.mixed().nullable(),
  "geospatial-id": yup.string().nullable(),
});

export const solutionFormDependencies: Array<[string, string]> = [
  ["outcome.unit", "outcome.value"],
  ["outcome.value", "outcome.unit"],
  ["latitude", "longitude"],
  ["longitude", "latitude"],
];

const optionalScoreSchema = yup
  .number()
  .nullable()
  .transform((value, originalValue) =>
    originalValue === "" || originalValue === null ? undefined : value,
  )
  .min(0, "Score should be over 0")
  .max(10, "Score should be less than 10")
  .test(
    "one-decimal-place",
    "Score should be to one decimal place",
    (value) =>
      value === undefined ||
      value === null ||
      Math.abs(value * 10 - Math.round(value * 10)) < Number.EPSILON * 10,
  );

const optionalBenefitsSchema = yup.object({
  quantity: yup.string(),
  description: yup.string(),
});

export const pillarsSchema = yup.object({
  carbon: yup.object({
    scores: yup.object({ total: optionalScoreSchema }),
    benefits: optionalBenefitsSchema,
  }),
  biodiversity: yup.object({
    scores: yup.object({ total: optionalScoreSchema }),
    benefits: optionalBenefitsSchema,
  }),
  people: yup.object({
    scores: yup.object({ total: optionalScoreSchema }),
    benefits: optionalBenefitsSchema,
  }),
});

export const commercialInformationSchema = yup.object().shape({
  "minimum-order": yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .typeError("Must be a number")
    .min(0, "Can't be a negative number"),
});

const createNarrativeFieldSchema = (maxWords: number) =>
  yup.string().test({
    name: "narrative-word-count",
    message: wordLimitMessage(maxWords),
    test: (value) => !value || countHtmlTextWords(value) <= maxWords,
  });

export const shortDescriptionSchema = yup.string().test({
  name: "short-description-word-count",
  message: wordLimitMessage(shortDescriptionMaxWords),
  test: (value) => !value || countWords(value) <= shortDescriptionMaxWords,
});

export const narrativeSchema = yup.object().shape({
  "project-information": createNarrativeFieldSchema(
    richTextMaxWords["project-information"],
  ),
  "area-through-time": createNarrativeFieldSchema(
    richTextMaxWords["area-through-time"],
  ),
  "good-for-people": createNarrativeFieldSchema(
    richTextMaxWords["good-for-people"],
  ),
  "good-for-earth": createNarrativeFieldSchema(
    richTextMaxWords["good-for-earth"],
  ),
});

export type SatelliteImagePair = {
  mediaLink?: string;
  caption?: string;
};

export const satelliteImageItemSchema = yup
  .object({
    mediaLink: createUrlSchema(urlRegex, "Must be a valid URL", true),
    caption: yup.string(),
    satelliteImageCounter: yup.number(),
  })
  .test({
    name: "paired-satellite-image",
    test: (value: SatelliteImagePair | undefined, context) => {
      if (!value?.caption || value.mediaLink) return true;

      return context.createError({
        path: `${context.path}.mediaLink`,
        message: "Media Link is required when Caption is provided",
      });
    },
  });

export const satelliteImagerySchema = yup.array().of(satelliteImageItemSchema);
